import type {
  StrategyRdComposerConfluenceSlot,
  StrategyRdComposerSetupSlot,
  StrategyRdConcept,
  StrategyRdConceptSetting,
  StrategyRdPatternPolarity,
  StrategyRdSlotSide,
} from "../types/strategyRd";
import {
  normalizeStrategyRdLevelPolarity,
  normalizeStrategyRdPolarity,
  normalizeStrategyRdPoolPolarity,
  normalizeStrategyRdSlotSide,
} from "./strategyRdComposer";

export type StrategyRdSettingUiValues = Record<string, string>;

function asSettingString(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    return value
      .map((item) => asSettingString(item).trim())
      .filter(Boolean)
      .join(",");
  }
  return "";
}

function parseSettingMultiValues(raw: string | null | undefined): string[] {
  return (raw || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * UI token for a catalog enum option whose `value` is `null` ("Any" = no limit).
 * Never sent: the param is omitted so BE applies its null default.
 */
export const STRATEGY_RD_SETTING_ANY = "__any__";

export function strategyRdSettingIsEnumMulti(
  setting:
    | (Pick<StrategyRdConceptSetting, "value_kind" | "key"> & { default?: unknown })
    | null
    | undefined
): boolean {
  if (!setting) return false;
  const kind = (setting.value_kind || "").trim().toLowerCase();
  if (kind === "enum_multi" || kind === "string_list" || kind === "multi_enum") return true;
  // BE marks multi-select enums (Killzone sessions) by a list default.
  if (Array.isArray(setting.default)) return true;
  return (setting.key || "").trim() === "scopes" && kind !== "enum";
}

/** Whether `visible_when` matches current UI values (all keys must match). */
export function strategyRdMatchesVisibleWhen(
  visibleWhen: Record<string, string | string[]> | null | undefined,
  uiValues: StrategyRdSettingUiValues
): boolean {
  if (!visibleWhen || typeof visibleWhen !== "object") return true;
  for (const [key, expected] of Object.entries(visibleWhen)) {
    const actual = (uiValues[key] ?? "").trim();
    if (Array.isArray(expected)) {
      if (!expected.map((v) => String(v).trim()).includes(actual)) return false;
      continue;
    }
    if (String(expected).trim() !== actual) return false;
  }
  return true;
}

const LEVEL_EXTREME_SETUP_IDS = new Set([
  "session_first_candle",
  "pdh_pdl",
  "pwh_pwl",
  "pmh_pml",
  "swing_hl",
  "developing_day_hl",
]);

/** When BE omits settings[] but marks level_extremes (ORB / PDH / week / month / swing H-L). */
export const STRATEGY_RD_LEVEL_EXTREMES_SETTINGS: StrategyRdConceptSetting[] = [
  {
    key: "polarity",
    label: "Level",
    value_kind: "enum",
    required: true,
    default: "both",
    enum_values: [
      { value: "high", label: "High" },
      { value: "low", label: "Low" },
      { value: "both", label: "Both" },
    ],
  },
  {
    key: "side",
    label: "Trade side",
    value_kind: "enum",
    default: "both",
    enum_values: [
      { value: "long", label: "Long" },
      { value: "short", label: "Short" },
      { value: "both", label: "High Long · Low Short" },
      { value: "invert", label: "High Short · Low Long" },
    ],
  },
];

/** When BE omits settings[] for clear_of_opposing_sr. */
export const STRATEGY_RD_CLEAR_OF_OPPOSING_SR_SETTINGS: StrategyRdConceptSetting[] = [
  {
    key: "avoid_quartile",
    label: "Avoid quartile",
    value_kind: "enum",
    required: false,
    default: 0.25,
    param: true,
    enum_values: [
      { value: "0.1", label: "10%" },
      { value: "0.25", label: "25%" },
      { value: "0.33", label: "33%" },
      { value: "0.5", label: "50%" },
    ],
  },
];

/** When BE omits settings[] for htf_bias. */
export const STRATEGY_RD_HTF_BIAS_SETTINGS: StrategyRdConceptSetting[] = [
  {
    key: "scope",
    label: "Scope",
    value_kind: "enum",
    required: true,
    default: "day",
    param: true,
    enum_values: [
      { value: "day", label: "Daily" },
      { value: "week", label: "Weekly" },
      { value: "month", label: "Monthly" },
    ],
  },
  {
    key: "allow_neutral",
    label: "Allow neutral",
    value_kind: "boolean",
    required: false,
    default: false,
    param: true,
    enum_values: [
      { value: "false", label: "No" },
      { value: "true", label: "Yes" },
    ],
  },
  {
    key: "side",
    label: "Trade side",
    value_kind: "enum",
    default: "both",
    enum_values: [
      { value: "long", label: "Long" },
      { value: "short", label: "Short" },
      { value: "both", label: "Both" },
    ],
  },
];

/** Zones keep value `reject`. Label is Break through — behaviour is unchanged. */
const LOCATION_ZONE_INTERACTION_ENUM: StrategyRdConceptSetting["enum_values"] = [
  {
    value: "respect",
    label: "Respect",
    description:
      "Tag the zone and hold in favor of trade side. Used when price respects the zone instead of closing through it.",
  },
  {
    value: "reject",
    label: "Break through (opposing zone)",
    description:
      "Long arms a bearish zone and fills on a close above its top. Short arms a bullish zone and fills on a close below its bottom.",
  },
];

/** Swing / prior H/L — Respect or Break. Legacy reject normalizes to break. */
const LOCATION_LEVEL_INTERACTION_ENUM: StrategyRdConceptSetting["enum_values"] = [
  {
    value: "respect",
    label: "Respect",
    description:
      "Classic wick reclaim. Tag the swing or prior high/low and close back on the hold side.",
  },
  {
    value: "break",
    label: "Break",
    description: "Close through that high/low in the trade-side direction.",
  },
];

/** Events stay Respect / Reject — not the zone Break-through label. */
const LOCATION_EVENT_INTERACTION_ENUM: StrategyRdConceptSetting["enum_values"] = [
  {
    value: "respect",
    label: "Respect",
    description:
      "Same-direction confirmation. The event agrees with trade side. Confirm only: it does not own Enter.",
  },
  {
    value: "reject",
    label: "Reject",
    description:
      "Failed opposing event. The other side's break or sweep fails before entry. Confirm only: it does not own Enter.",
  },
];

const LOCATION_TRENDLINE_INTERACTION_ENUM: StrategyRdConceptSetting["enum_values"] = [
  { value: "respect", label: "Respect" },
  { value: "break", label: "Break" },
  { value: "break_retest", label: "Break + retest" },
];

const LOCATION_STRUCTURE_ENUM: StrategyRdConceptSetting["enum_values"] = [
  { value: "auto", label: "Auto" },
  { value: "bull", label: "Bullish" },
  { value: "bear", label: "Bearish" },
];

const LOCATION_TIMING_ENUM: StrategyRdConceptSetting["enum_values"] = [
  { value: "either", label: "Either" },
  { value: "preexisting", label: "Pre-existing" },
  { value: "displacement", label: "After setup" },
];

/** Fractal window L=R — only swing_hl (setup) and near_swing (location). */
export const STRATEGY_RD_SWING_LENGTH_SETTING: StrategyRdConceptSetting = {
  key: "swing_length",
  label: "Swing length",
  value_kind: "enum",
  required: false,
  default: 3,
  param: true,
  enum_values: [
    { value: "2", label: "2 bars" },
    { value: "3", label: "3 bars (default)" },
    { value: "5", label: "5 bars" },
    { value: "10", label: "10 bars" },
  ],
};

/** Prior day/week/month scopes for near_prior_sr (multi). */
export const STRATEGY_RD_NEAR_PRIOR_SR_SCOPES_SETTING: StrategyRdConceptSetting = {
  key: "scopes",
  label: "Prior scopes",
  value_kind: "enum_multi",
  required: false,
  default: "week",
  param: true,
  enum_values: [
    { value: "day", label: "Day (PDH/PDL)" },
    { value: "week", label: "Week (PWH/PWL)" },
    { value: "month", label: "Month (PMH/PML)" },
  ],
};

export const STRATEGY_RD_LOCATION_INTERACTION_IDS = new Set([
  "fvg_overlap",
  "near_ob",
  "near_breaker",
  "near_swing",
  "near_prior_sr",
  "prior_liq_sweep",
  "prior_liq_run",
  "bos",
  "choch",
  "trendline",
]);

/** Zone locations can own the Enter fill. Event locations only confirm. */
export const STRATEGY_RD_LOCATION_ZONE_IDS = new Set([
  "fvg_overlap",
  "near_ob",
  "near_breaker",
  "near_swing",
  "near_prior_sr",
  "trendline",
]);

export const STRATEGY_RD_LOCATION_EVENT_IDS = new Set([
  "prior_liq_sweep",
  "prior_liq_run",
  "bos",
  "choch",
]);

/** Level-style Location chips — Interaction includes Break (not break_retest). */
export const STRATEGY_RD_LOCATION_LEVEL_IDS = new Set(["near_swing", "near_prior_sr"]);

/** Liquidity Pool setups — polarity is Pool (high/low/both or buyside/sellside), not trade side. */
export const STRATEGY_RD_POOL_SETUP_IDS = new Set(["liq_sweep", "liq_run"]);

/** When BE omits settings[] for liq_sweep / liq_run. Pool = which swing was taken. */
export const STRATEGY_RD_LIQ_POOL_SETTINGS: StrategyRdConceptSetting[] = [
  {
    key: "polarity",
    label: "Pool",
    value_kind: "enum",
    required: true,
    default: "both",
    enum_values: [
      { value: "high", label: "High (buyside)" },
      { value: "low", label: "Low (sellside)" },
      { value: "both", label: "Both" },
    ],
  },
  {
    key: "side",
    label: "Trade side",
    value_kind: "enum",
    default: "both",
    enum_values: [
      { value: "long", label: "Long" },
      { value: "short", label: "Short" },
      { value: "both", label: "High Long · Low Short" },
      { value: "invert", label: "Buyside Short · Sellside Long" },
    ],
  },
];

export function strategyRdConceptIsLocationInteraction(
  concept: Pick<StrategyRdConcept, "id" | "location_interaction"> | null | undefined
): boolean {
  if (concept?.location_interaction === true) return true;
  const id = (concept?.id || "").trim();
  return id ? STRATEGY_RD_LOCATION_INTERACTION_IDS.has(id) : false;
}

export function strategyRdLocationIsZone(
  conceptId: string | null | undefined
): boolean {
  const id = (conceptId || "").trim();
  return id ? STRATEGY_RD_LOCATION_ZONE_IDS.has(id) : false;
}

export function strategyRdLocationIsEvent(
  conceptId: string | null | undefined
): boolean {
  const id = (conceptId || "").trim();
  return id ? STRATEGY_RD_LOCATION_EVENT_IDS.has(id) : false;
}

export function strategyRdLocationIsLevel(
  conceptId: string | null | undefined
): boolean {
  const id = (conceptId || "").trim();
  return id ? STRATEGY_RD_LOCATION_LEVEL_IDS.has(id) : false;
}

/** Fallback interaction/structure/timing when catalog settings[] omits them. */
export function strategyRdLocationInteractionSettings(
  conceptId: string | null | undefined
): StrategyRdConceptSetting[] {
  const id = (conceptId || "").trim();
  const isTrendline = id === "trendline";
  const isLevel = STRATEGY_RD_LOCATION_LEVEL_IDS.has(id);
  const isEvent = STRATEGY_RD_LOCATION_EVENT_IDS.has(id);
  const interactionEnum = isTrendline
    ? LOCATION_TRENDLINE_INTERACTION_ENUM
    : isLevel
      ? LOCATION_LEVEL_INTERACTION_ENUM
      : isEvent
        ? LOCATION_EVENT_INTERACTION_ENUM
        : LOCATION_ZONE_INTERACTION_ENUM;
  const timingDefault = isTrendline || isLevel ? "preexisting" : "either";
  const rows: StrategyRdConceptSetting[] = [
    {
      key: "interaction",
      label: "Interaction",
      value_kind: "enum",
      required: true,
      default: "respect",
      param: true,
      enum_values: interactionEnum,
    },
    {
      key: "structure",
      label: "Structure",
      value_kind: "enum",
      required: false,
      default: "auto",
      param: true,
      enum_values: LOCATION_STRUCTURE_ENUM,
    },
    {
      key: "timing",
      label: "Timing",
      value_kind: "enum",
      required: false,
      default: timingDefault,
      param: true,
      enum_values: LOCATION_TIMING_ENUM,
    },
  ];
  if (id === "near_prior_sr") {
    rows.push(STRATEGY_RD_NEAR_PRIOR_SR_SCOPES_SETTING);
  }
  if (id === "near_swing") {
    rows.push(STRATEGY_RD_SWING_LENGTH_SETTING);
  }
  return rows;
}

/**
 * Levels no longer list Reject. A saved `reject` on a swing/prior chip maps to `break`
 * when that is the catalog option. Zones and events still post `reject`.
 */
export function strategyRdCoerceLocationInteraction(
  interaction: string | null | undefined,
  enumValues: StrategyRdConceptSetting["enum_values"]
): string {
  const value = (interaction || "").trim().toLowerCase();
  if (!value) return "";
  const allowed = new Set(
    (enumValues || [])
      .map((row) => String(row?.value ?? "").trim().toLowerCase())
      .filter(Boolean)
  );
  if (value === "reject" && !allowed.has("reject") && allowed.has("break")) return "break";
  return value;
}

/**
 * Preview/Scan echo the normalized composer. Replace stale level `reject` with the
 * value BE returned (`break`) instead of keeping the pre-request slot.
 */
export function strategyRdMergeComposerEchoInteractions(
  slots: StrategyRdComposerConfluenceSlot[],
  composer: Record<string, unknown> | null | undefined
): StrategyRdComposerConfluenceSlot[] {
  const raw = composer && Array.isArray(composer.confluences) ? composer.confluences : null;
  if (!raw || raw.length === 0) return slots;
  let changed = false;
  const next = slots.map((slot, index) => {
    const echo = raw[index];
    if (!echo || typeof echo !== "object" || Array.isArray(echo)) return slot;
    const rec = echo as Record<string, unknown>;
    const id = String(rec.id ?? "").trim();
    if (id && id !== slot.id) return slot;
    const params =
      rec.params && typeof rec.params === "object" && !Array.isArray(rec.params)
        ? (rec.params as Record<string, unknown>)
        : null;
    if (!params || params.interaction == null) return slot;
    const interaction = String(params.interaction).trim().toLowerCase();
    if (!interaction) return slot;
    const current = String(slot.params?.interaction ?? "").trim().toLowerCase();
    if (current === interaction) return slot;
    changed = true;
    return {
      ...slot,
      params: { ...(slot.params || {}), interaction },
    };
  });
  return changed ? next : slots;
}

/**
 * Family key for remembering Reject preference across adds.
 * zone = FVG/OB/Breaker · level = swing/prior H/L · event = sweep/run/BOS/CHoCH · line = trendline
 */
export function strategyRdLocationInteractionFamily(
  conceptId: string | null | undefined
): "zone" | "event" | "line" | "level" | null {
  const id = (conceptId || "").trim();
  if (STRATEGY_RD_LOCATION_LEVEL_IDS.has(id)) return "level";
  if (STRATEGY_RD_LOCATION_ZONE_IDS.has(id) && id !== "trendline") return "zone";
  if (id === "trendline") return "line";
  if (STRATEGY_RD_LOCATION_EVENT_IDS.has(id)) return "event";
  return null;
}

export function strategyRdConceptIsLevelExtremesSetup(
  concept: Pick<StrategyRdConcept, "id" | "level_extremes"> | null | undefined
): boolean {
  const id = (concept?.id || "").trim();
  return concept?.level_extremes === true || (id ? LEVEL_EXTREME_SETUP_IDS.has(id) : false);
}

function mergeMissingSettings(
  list: StrategyRdConceptSetting[],
  extras: StrategyRdConceptSetting[]
): StrategyRdConceptSetting[] {
  const keys = new Set(list.map((s) => (s.key || "").trim()).filter(Boolean));
  const out = [...list];
  for (const row of extras) {
    if (!keys.has(row.key)) out.push(row);
  }
  return out;
}

/**
 * Composer field order: Interaction (location) → Polarity → Trade side → remaining
 * (stable). Keeps directional controls left of dense concept params.
 */
export function sortStrategyRdSettingsUiOrder(
  list: StrategyRdConceptSetting[]
): StrategyRdConceptSetting[] {
  const rank = (s: StrategyRdConceptSetting): number => {
    const k = (s.key || "").trim();
    if (k === "interaction") return 0;
    if (k === "polarity") return 1;
    if (k === "side" && !s.param) return 2;
    return 10;
  };
  return list
    .map((setting, index) => ({ setting, index }))
    .sort((a, b) => {
      const diff = rank(a.setting) - rank(b.setting);
      return diff !== 0 ? diff : a.index - b.index;
    })
    .map(({ setting }) => setting);
}

/** Invert Trade-side label for Level / Pattern / Pool polarity forks. */
function invertTradeSideLabel(
  kind: StrategyRdSetupPolarityKind
): string {
  if (kind === "pattern") return "Bull Short · Bear Long";
  if (kind === "pool") return "Buyside Short · Sellside Long";
  if (kind === "level") return "High Short · Low Long";
  return "Invert";
}

const STRATEGY_RD_SMA_LENGTH_VALUES = [5, 8, 9, 10, 20, 21, 30, 50, 100, 200] as const;
const STRATEGY_RD_EMA_LENGTH_VALUES = [5, 8, 9, 10, 12, 20, 21, 50, 100, 200] as const;

function maLengthEnumValues(
  kind: "sma" | "ema",
  opts?: { labeled?: boolean }
): NonNullable<StrategyRdConceptSetting["enum_values"]> {
  const values = kind === "sma" ? STRATEGY_RD_SMA_LENGTH_VALUES : STRATEGY_RD_EMA_LENGTH_VALUES;
  const prefix = kind === "sma" ? "SMA" : "EMA";
  return values.map((n) => ({
    value: String(n),
    label: opts?.labeled === false ? String(n) : `${prefix} ${n}`,
  }));
}

/** SMA cross — Fast 50 / Slow 200 · Trade side with Invert (golden/death). */
export const STRATEGY_RD_SMA_CROSS_SETTINGS: StrategyRdConceptSetting[] = [
  {
    key: "fast",
    label: "Fast length",
    value_kind: "enum",
    required: true,
    default: 50,
    param: true,
    enum_values: maLengthEnumValues("sma", { labeled: false }),
  },
  {
    key: "slow",
    label: "Slow length",
    value_kind: "enum",
    required: true,
    default: 200,
    param: true,
    enum_values: maLengthEnumValues("sma", { labeled: false }),
  },
  {
    key: "side",
    label: "Trade side",
    value_kind: "enum",
    default: "both",
    enum_values: [
      { value: "long", label: "Long" },
      { value: "short", label: "Short" },
      { value: "both", label: "Bull Long · Bear Short" },
      { value: "invert", label: "Bull Short · Bear Long" },
    ],
  },
];

/** EMA cross — Fast 10 / Slow 20 · Trade side with Invert. */
export const STRATEGY_RD_EMA_CROSS_SETTINGS: StrategyRdConceptSetting[] = [
  {
    key: "fast",
    label: "Fast length",
    value_kind: "enum",
    required: true,
    default: 10,
    param: true,
    enum_values: maLengthEnumValues("ema", { labeled: false }),
  },
  {
    key: "slow",
    label: "Slow length",
    value_kind: "enum",
    required: true,
    default: 20,
    param: true,
    enum_values: maLengthEnumValues("ema", { labeled: false }),
  },
  {
    key: "side",
    label: "Trade side",
    value_kind: "enum",
    default: "both",
    enum_values: [
      { value: "long", label: "Long" },
      { value: "short", label: "Short" },
      { value: "both", label: "Bull Long · Bear Short" },
      { value: "invert", label: "Bull Short · Bear Long" },
    ],
  },
];

export function strategyRdIsMaCrossSetupId(id: string | null | undefined): boolean {
  const key = (id || "").trim();
  return key === "sma_cross" || key === "ema_cross";
}

/** Setups with Trade side Both/Invert (golden/death or pullback book) and no Pattern control. */
export function strategyRdIsMaBookSetupId(id: string | null | undefined): boolean {
  const key = (id || "").trim();
  return strategyRdIsMaCrossSetupId(key) || key === "pullback_to_ma";
}

/** Pullback to MA — type + length + Trade side (default EMA 10). */
export const STRATEGY_RD_PULLBACK_TO_MA_SETTINGS: StrategyRdConceptSetting[] = [
  {
    key: "ma_kind",
    label: "MA type",
    value_kind: "enum",
    required: true,
    default: "ema",
    param: true,
    enum_values: [
      { value: "ema", label: "EMA" },
      { value: "sma", label: "SMA" },
    ],
  },
  {
    key: "ma_length",
    label: "MA length",
    value_kind: "enum",
    required: true,
    default: 10,
    param: true,
    enum_values: maLengthEnumValues("ema", { labeled: false }),
  },
  {
    key: "side",
    label: "Trade side",
    value_kind: "enum",
    default: "both",
    enum_values: [
      { value: "long", label: "Long" },
      { value: "short", label: "Short" },
      { value: "both", label: "Bull Long · Bear Short" },
      { value: "invert", label: "Bull Short · Bear Long" },
    ],
  },
];

/** Shock / expansion bar — |Δclose| / |prior close| ≥ Min %. */
export const STRATEGY_RD_PCT_BREAK_BAR_SETTINGS: StrategyRdConceptSetting[] = [
  {
    key: "min_pct",
    label: "Min % move",
    value_kind: "enum",
    required: true,
    default: 1,
    param: true,
    enum_values: [
      { value: "0.25", label: "0.25%" },
      { value: "0.5", label: "0.5%" },
      { value: "0.75", label: "0.75%" },
      { value: "1", label: "1%" },
      { value: "1.5", label: "1.5%" },
      { value: "2", label: "2%" },
      { value: "2.5", label: "2.5%" },
      { value: "3", label: "3%" },
      { value: "4", label: "4%" },
      { value: "5", label: "5%" },
    ],
  },
  {
    key: "require_vol_expand",
    label: "Require volume expansion",
    value_kind: "boolean",
    required: false,
    default: false,
    param: true,
    enum_values: [
      { value: "false", label: "No" },
      { value: "true", label: "Yes" },
    ],
  },
  {
    key: "side",
    label: "Trade side",
    value_kind: "enum",
    default: "both",
    enum_values: [
      { value: "long", label: "Long" },
      { value: "short", label: "Short" },
      { value: "both", label: "Both" },
      { value: "invert", label: "Invert" },
    ],
  },
];

/**
 * Weinstein Stage analysis — breakout (1→2 / 3→4) or MA regime flip.
 * Breakout-only gates hide when signal_mode === "regime".
 */
export const STRATEGY_RD_STAGE_SETTINGS: StrategyRdConceptSetting[] = [
  {
    key: "signal_mode",
    label: "Signal mode",
    value_kind: "enum",
    required: true,
    default: "breakout",
    param: true,
    enum_values: [
      { value: "breakout", label: "Base breakout" },
      { value: "regime", label: "MA regime flip" },
    ],
  },
  {
    key: "ma_length",
    label: "SMA length",
    value_kind: "enum",
    required: true,
    default: 30,
    param: true,
    enum_values: maLengthEnumValues("sma", { labeled: false }),
  },
  {
    key: "min_base_bars",
    label: "Min base bars",
    value_kind: "enum",
    required: false,
    default: 10,
    param: true,
    visible_when: { signal_mode: "breakout" },
    enum_values: [
      { value: "5", label: "5" },
      { value: "8", label: "8" },
      { value: "10", label: "10" },
      { value: "12", label: "12" },
      { value: "15", label: "15" },
      { value: "20", label: "20" },
      { value: "25", label: "25" },
      { value: "30", label: "30" },
    ],
  },
  {
    key: "require_vol_expand",
    label: "Require volume expansion",
    value_kind: "boolean",
    required: false,
    default: true,
    param: true,
    visible_when: { signal_mode: "breakout" },
    enum_values: [
      { value: "false", label: "No" },
      { value: "true", label: "Yes" },
    ],
  },
  {
    key: "side",
    label: "Trade side",
    value_kind: "enum",
    default: "both",
    enum_values: [
      { value: "long", label: "Long" },
      { value: "short", label: "Short" },
      { value: "both", label: "Both" },
      { value: "invert", label: "Invert" },
    ],
  },
];

/**
 * When catalog `side` already exists without `invert`, append it so polarity-both
 * recipes can send side:"invert" + polarity_sides (not a neutral both).
 */
function ensureInvertTradeSideOption(
  list: StrategyRdConceptSetting[],
  kind: StrategyRdSetupPolarityKind
): StrategyRdConceptSetting[] {
  const sideIx = list.findIndex((s) => s.key === "side" && !s.param);
  if (sideIx < 0) return list;
  // MA cross / pullback / pct break: Trade side without Pattern polarity still allows Invert.
  const side = list[sideIx];
  const enums = Array.isArray(side.enum_values) ? [...side.enum_values] : [];
  const hasInvert = enums.some(
    (row) => String(row.value || "").trim().toLowerCase() === "invert"
  );
  if (hasInvert) return list;
  const next = [...list];
  next[sideIx] = {
    ...side,
    enum_values: [
      ...enums,
      { value: "invert", label: invertTradeSideLabel(kind) },
    ],
  };
  return next;
}

/** Expand Fast / Slow / Length enums to the full SMA or EMA set when catalog is truncated. */
function ensureMaLengthEnums(
  list: StrategyRdConceptSetting[],
  kind: "sma" | "ema"
): StrategyRdConceptSetting[] {
  const full = maLengthEnumValues(kind, {
    // Cross Fast/Slow use bare numbers; confluence length keeps SMA/EMA labels.
    labeled: true,
  });
  const bare = maLengthEnumValues(kind, { labeled: false });
  let out = list;
  for (const key of ["fast", "slow", "length", "ma_length"]) {
    const ix = out.findIndex((s) => s.key === key && s.param);
    if (ix < 0) continue;
    const row = out[ix]!;
    const existing = Array.isArray(row.enum_values) ? [...row.enum_values] : [];
    const useBare = key === "fast" || key === "slow" || key === "ma_length";
    const source = useBare ? bare : full;
    const have = new Set(
      existing.map((e) => String(e.value ?? "").trim()).filter(Boolean)
    );
    const merged = [...existing];
    for (const opt of source) {
      if (!have.has(String(opt.value ?? "").trim())) merged.push(opt);
    }
    merged.sort((a, b) => Number(a.value) - Number(b.value));
    if (merged.length === existing.length) continue;
    if (out === list) out = [...list];
    out[ix] = { ...row, enum_values: merged };
  }
  return out;
}

export function strategyRdConceptSettings(
  concept:
    | (Pick<StrategyRdConcept, "id" | "settings" | "level_extremes" | "location_interaction"> &
        Partial<Pick<StrategyRdConcept, "role">>)
    | null
    | undefined
): StrategyRdConceptSetting[] {
  const rawSettings = concept?.settings;
  let list = Array.isArray(rawSettings) ? [...rawSettings] : [];
  const id = (concept?.id || "").trim();

  if (strategyRdConceptIsLevelExtremesSetup(concept)) {
    list = mergeMissingSettings(list, STRATEGY_RD_LEVEL_EXTREMES_SETTINGS);
    // Fractal L=R only on swing H/L — not PDH / week / month / developing day / ORB.
    if (id === "swing_hl" || id === "swing_high_low") {
      list = mergeMissingSettings(list, [STRATEGY_RD_SWING_LENGTH_SETTING]);
    }
    const order = new Map([
      ["polarity", 0],
      ["side", 1],
      ["swing_length", 2],
    ]);
    list.sort(
      (a, b) =>
        (order.get(a.key) ?? 99) - (order.get(b.key) ?? 99)
    );
    return sortStrategyRdSettingsUiOrder(ensureInvertTradeSideOption(list, "level"));
  }

  if (id === "clear_of_opposing_sr") {
    return sortStrategyRdSettingsUiOrder(
      mergeMissingSettings(list, STRATEGY_RD_CLEAR_OF_OPPOSING_SR_SETTINGS)
    );
  }
  if (STRATEGY_RD_POOL_SETUP_IDS.has(id)) {
    list = mergeMissingSettings(list, STRATEGY_RD_LIQ_POOL_SETTINGS);
    const order = new Map([
      ["polarity", 0],
      ["side", 1],
    ]);
    list.sort(
      (a, b) =>
        (order.get(a.key) ?? 99) - (order.get(b.key) ?? 99)
    );
    // ICT Pool uses high/low — invert label matches Level (High Short · Low Long).
    return sortStrategyRdSettingsUiOrder(ensureInvertTradeSideOption(list, "level"));
  }
  if (id === "htf_bias") {
    list = mergeMissingSettings(list, STRATEGY_RD_HTF_BIAS_SETTINGS);
    const order = new Map([
      ["scope", 0],
      ["allow_neutral", 1],
      ["side", 2],
      ["candle_type", 3],
    ]);
    list.sort(
      (a, b) =>
        (order.get(a.key) ?? 99) - (order.get(b.key) ?? 99)
    );
    return sortStrategyRdSettingsUiOrder(list);
  }

  if (id === "sma_cross") {
    list = mergeMissingSettings(list, STRATEGY_RD_SMA_CROSS_SETTINGS);
    list = ensureMaLengthEnums(list, "sma");
    const order = new Map([
      ["fast", 0],
      ["slow", 1],
      ["side", 2],
    ]);
    list.sort(
      (a, b) =>
        (order.get(a.key) ?? 99) - (order.get(b.key) ?? 99)
    );
    return sortStrategyRdSettingsUiOrder(ensureInvertTradeSideOption(list, "pattern"));
  }
  if (id === "ema_cross") {
    list = mergeMissingSettings(list, STRATEGY_RD_EMA_CROSS_SETTINGS);
    list = ensureMaLengthEnums(list, "ema");
    const order = new Map([
      ["fast", 0],
      ["slow", 1],
      ["side", 2],
    ]);
    list.sort(
      (a, b) =>
        (order.get(a.key) ?? 99) - (order.get(b.key) ?? 99)
    );
    return sortStrategyRdSettingsUiOrder(ensureInvertTradeSideOption(list, "pattern"));
  }
  if (id === "pullback_to_ma") {
    list = mergeMissingSettings(list, STRATEGY_RD_PULLBACK_TO_MA_SETTINGS);
    list = ensureMaLengthEnums(list, "ema");
    const order = new Map([
      ["ma_kind", 0],
      ["ma_length", 1],
      ["side", 2],
    ]);
    list.sort(
      (a, b) =>
        (order.get(a.key) ?? 99) - (order.get(b.key) ?? 99)
    );
    return sortStrategyRdSettingsUiOrder(ensureInvertTradeSideOption(list, "pattern"));
  }
  if (id === "pct_break_bar") {
    list = mergeMissingSettings(list, STRATEGY_RD_PCT_BREAK_BAR_SETTINGS);
    const order = new Map([
      ["min_pct", 0],
      ["require_vol_expand", 1],
      ["side", 2],
    ]);
    list.sort(
      (a, b) =>
        (order.get(a.key) ?? 99) - (order.get(b.key) ?? 99)
    );
    return sortStrategyRdSettingsUiOrder(ensureInvertTradeSideOption(list, "none"));
  }
  if (id === "stage" || id === "stage_2" || id === "stage_4") {
    const role = String(concept?.role || "").trim().toLowerCase();
    // Confluence catalog row is SMA length, Trade side, and timeframe.
    // Setup controls (signal mode, base bars, volume) stay on the setup row.
    if (role === "confluence" && list.length > 0) {
      return sortStrategyRdSettingsUiOrder(list);
    }
    list = mergeMissingSettings(list, STRATEGY_RD_STAGE_SETTINGS);
    list = ensureMaLengthEnums(list, "sma");
    const order = new Map([
      ["signal_mode", 0],
      ["ma_length", 1],
      ["min_base_bars", 2],
      ["require_vol_expand", 3],
      ["side", 4],
    ]);
    list.sort(
      (a, b) =>
        (order.get(a.key) ?? 99) - (order.get(b.key) ?? 99)
    );
    return sortStrategyRdSettingsUiOrder(ensureInvertTradeSideOption(list, "none"));
  }

  // Confluence SMA / EMA — expand length enums when catalog is truncated.
  if (id === "sma" || /(^|_)sma($|_)/.test(id)) {
    list = ensureMaLengthEnums(list, "sma");
  } else if (id === "ema" || /(^|_)ema($|_)/.test(id)) {
    list = ensureMaLengthEnums(list, "ema");
  }

  if (strategyRdConceptIsLocationInteraction(concept)) {
    list = mergeMissingSettings(list, strategyRdLocationInteractionSettings(id));
    // Interaction options come from catalog enum_values. Do not replace them.
    if (id === "near_prior_sr") {
      const scopesIx = list.findIndex((s) => (s.key || "").trim() === "scopes");
      if (scopesIx >= 0) {
        list[scopesIx] = { ...list[scopesIx]!, default: "week" };
      }
    }
    const order = new Map([
      ["interaction", 0],
      ["scopes", 1],
      ["side", 2],
      ["candle_type", 3],
      ["timing", 4],
      ["swing_length", 5],
      ["structure", 6],
    ]);
    list.sort(
      (a, b) =>
        (order.get(a.key) ?? 99) - (order.get(b.key) ?? 99)
    );
    return sortStrategyRdSettingsUiOrder(list);
  }

  // Pattern (and any catalog polarity fork): ensure Invert on Trade side.
  return sortStrategyRdSettingsUiOrder(
    ensureInvertTradeSideOption(list, strategyRdSetupPolarityKind(list))
  );
}

/** Defaults for every setting (including currently hidden ones). */
export function strategyRdDefaultSettingUiValues(
  settings: StrategyRdConceptSetting[] | null | undefined
): StrategyRdSettingUiValues {
  const out: StrategyRdSettingUiValues = {};
  for (const s of settings || []) {
    const key = (s.key || "").trim();
    if (!key) continue;
    const def = asSettingString(s.default);
    if (def) out[key] = def;
  }
  return out;
}

/**
 * Rebuild UI values from an existing slot + catalog settings.
 * Param keys read from slot.params; side / polarity / candle_type from top-level.
 */
export function strategyRdUiValuesFromSlot(
  settings: StrategyRdConceptSetting[] | null | undefined,
  slot:
    | Pick<StrategyRdComposerSetupSlot, "side" | "polarity" | "candle_type" | "params">
    | Pick<
        StrategyRdComposerConfluenceSlot,
        "side" | "polarity" | "candle_type" | "params" | "settings"
      >
    | null
    | undefined,
  fallbacks?: StrategyRdSettingUiValues
): StrategyRdSettingUiValues {
  const values = {
    ...strategyRdDefaultSettingUiValues(settings),
    ...(fallbacks || {}),
  };
  const params =
    (slot && "params" in slot && slot.params && typeof slot.params === "object"
      ? slot.params
      : null) ||
    (slot && "settings" in slot && slot.settings && typeof slot.settings === "object"
      ? slot.settings
      : null);
  for (const s of settings || []) {
    const key = (s.key || "").trim();
    if (!key) continue;
    if (s.param) {
      if (params && key in params) {
        if (params[key] === null && strategyRdSettingHasAnyOption(s)) {
          values[key] = STRATEGY_RD_SETTING_ANY;
          continue;
        }
        const v = asSettingString(params[key]);
        if (v) {
          values[key] =
            key === "interaction"
              ? strategyRdCoerceLocationInteraction(v, s.enum_values)
              : v;
        }
      }
      continue;
    }
    if (key === "side" && slot?.side != null && String(slot.side).trim()) {
      values.side = String(slot.side).trim();
      continue;
    }
    if (key === "polarity" && slot?.polarity != null && String(slot.polarity).trim()) {
      values.polarity = String(slot.polarity).trim();
      continue;
    }
    if (key === "candle_type" && slot?.candle_type != null && String(slot.candle_type).trim()) {
      values.candle_type = String(slot.candle_type).trim();
    }
  }
  return values;
}

export type StrategyRdBuiltSlot = {
  id: string;
  side?: StrategyRdSlotSide;
  polarity?: StrategyRdPatternPolarity | string;
  candle_type?: string;
  params?: Record<string, unknown>;
};

/**
 * Build a nested setup/confluence request slot from catalog settings + UI values.
 * - `side` → trade branch (top-level)
 * - `polarity` without param → top-level Pattern (bull|bear|both) or Level (high|low|both)
 * - `param: true` keys (incl. MA location polarity above|below) → params
 * Legacy (no settings[]): only side — BE treats as both polarity + trade.
 */
export function strategyRdBuildSlotFromSettings(
  concept: Pick<StrategyRdConcept, "id" | "settings"> | null | undefined,
  uiValues: StrategyRdSettingUiValues,
  options?: { omitCandleType?: boolean }
): StrategyRdBuiltSlot | null {
  const id = (concept?.id || "").trim();
  if (!id) return null;
  const settings = strategyRdConceptSettings(concept);
  const slot: StrategyRdBuiltSlot = { id };

  if (settings.length === 0) {
    const side = normalizeStrategyRdSlotSide(uiValues.side);
    if (side) slot.side = side;
    if (!options?.omitCandleType) {
      const tf = (uiValues.candle_type || "").trim();
      if (tf && tf !== "inherit") slot.candle_type = tf;
    }
    return slot;
  }

  for (const s of settings) {
    const key = (s.key || "").trim();
    if (!key) continue;
    if (s.visible_when && !strategyRdMatchesVisibleWhen(s.visible_when, uiValues)) {
      continue;
    }
    const raw = uiValues[key] ?? asSettingString(s.default);
    const v = asSettingString(raw).trim();
    // "Any" (null option) → omit; BE default is no limit.
    if (!v || v === STRATEGY_RD_SETTING_ANY) continue;

    if (s.param) {
      if (strategyRdSettingIsEnumMulti(s)) {
        const parts = parseSettingMultiValues(v);
        const coerced =
          key === "scopes"
            ? parts
                .map((part) => part.toLowerCase())
                .filter((part) => part === "day" || part === "week" || part === "month")
            : parts;
        if (coerced.length > 0) {
          slot.params = { ...(slot.params || {}), [key]: coerced };
        }
        continue;
      }
      let coerced: string | number | boolean = v;
      if (s.value_kind === "number" || /^-?\d+(\.\d+)?$/.test(v)) {
        const n = Number(v);
        if (!Number.isNaN(n)) coerced = n;
      } else if (s.value_kind === "boolean") {
        coerced = v === "true" || v === "1";
      } else if (key === "interaction") {
        coerced = strategyRdCoerceLocationInteraction(v, s.enum_values);
      }
      slot.params = { ...(slot.params || {}), [key]: coerced };
      continue;
    }
    if (key === "side") {
      const side = normalizeStrategyRdSlotSide(v);
      if (side) slot.side = side;
      continue;
    }
    if (key === "polarity") {
      const polarity =
        normalizeStrategyRdPolarity(v) ||
        normalizeStrategyRdLevelPolarity(v) ||
        normalizeStrategyRdPoolPolarity(v) ||
        v;
      if (polarity) slot.polarity = polarity;
      continue;
    }
    if (key === "candle_type") {
      if (options?.omitCandleType) continue;
      if (v !== "inherit") slot.candle_type = v;
    }
  }
  // Catalog may omit side / candle_type on Location chips — still honor UI values
  // when those keys are not part of the settings schema (not merely hidden).
  const settingKeys = new Set(settings.map((s) => (s.key || "").trim()).filter(Boolean));
  if (!slot.side && !settingKeys.has("side")) {
    const side = normalizeStrategyRdSlotSide(uiValues.side);
    if (side) slot.side = side;
  }
  if (!slot.candle_type && !options?.omitCandleType && !settingKeys.has("candle_type")) {
    const tf = (uiValues.candle_type || "").trim();
    if (tf && tf !== "inherit") slot.candle_type = tf;
  }
  return slot;
}

/** Apply built slot fields onto a setup composer slot (drops candle_type by default). */
export function strategyRdApplyBuiltSetupSlot(
  built: StrategyRdBuiltSlot | null,
  previous?: StrategyRdComposerSetupSlot | null
): StrategyRdComposerSetupSlot {
  if (!built) return { id: previous?.id || "" };
  return {
    id: built.id,
    ...(built.side ? { side: built.side } : {}),
    ...(built.polarity ? { polarity: built.polarity } : {}),
    ...(previous?.side_by_polarity ? { side_by_polarity: previous.side_by_polarity } : {}),
    ...(built.params && Object.keys(built.params).length > 0 ? { params: built.params } : {}),
    ...(previous?.resolved_ids ? { resolved_ids: previous.resolved_ids } : {}),
    ...(previous?.name ? { name: previous.name } : {}),
    ...(previous?.description ? { description: previous.description } : {}),
  };
}

/** Apply built slot fields onto a confluence composer slot. */
export function strategyRdApplyBuiltConfluenceSlot(
  built: StrategyRdBuiltSlot | null,
  previous?: StrategyRdComposerConfluenceSlot | null
): StrategyRdComposerConfluenceSlot {
  if (!built) return { id: previous?.id || "" };
  return {
    id: built.id,
    ...(built.side ? { side: built.side } : {}),
    ...(built.polarity ? { polarity: built.polarity } : {}),
    ...(previous?.side_by_polarity ? { side_by_polarity: previous.side_by_polarity } : {}),
    ...(built.candle_type
      ? { candle_type: built.candle_type }
      : previous?.candle_type
        ? { candle_type: previous.candle_type }
        : {}),
    ...(built.params && Object.keys(built.params).length > 0 ? { params: built.params } : {}),
    ...(previous?.name ? { name: previous.name } : {}),
    ...(previous?.description ? { description: previous.description } : {}),
    ...(previous?.optional === true ? { optional: true } : {}),
  };
}

export function strategyRdSettingEnumOptions(
  setting: Pick<StrategyRdConceptSetting, "enum_values"> | null | undefined
): StrategyRdSettingEnumOption[] {
  const enumValues = setting?.enum_values;
  if (!Array.isArray(enumValues)) return [];
  return enumValues
    .map((row) => {
      if (row && row.value === null) {
        return {
          value: STRATEGY_RD_SETTING_ANY,
          label: String(row.label ?? "").trim() || "Any",
        };
      }
      const label = String(row?.label ?? row?.value ?? "").trim();
      const description = String(row?.description ?? "").trim();
      const utcInLabel = /\bUTC\b/.test(description) && !label.includes(description);
      return {
        value: String(row?.value ?? "").trim(),
        // Killzone sessions carry their UTC hours in `description`.
        label: utcInLabel ? `${label} (${description})` : label,
        ...(description && !utcInLabel ? { description } : {}),
      };
    })
    .filter((row) => row.value);
}

/** A catalog enum option ready for a select: `null` ("Any") mapped to `STRATEGY_RD_SETTING_ANY`. */
export type StrategyRdSettingEnumOption = {
  value: string;
  label: string;
  description?: string;
};

/** True when the catalog enum lists a `null` ("Any") option. */
export function strategyRdSettingHasAnyOption(
  setting: Pick<StrategyRdConceptSetting, "enum_values"> | null | undefined
): boolean {
  return Array.isArray(setting?.enum_values) && setting!.enum_values!.some((row) => row?.value === null);
}

/** Catalog enum label for a setting value (e.g. swing_length 5 → "5 bars"). */
export function strategyRdSettingEnumLabel(
  setting: Pick<StrategyRdConceptSetting, "enum_values"> | null | undefined,
  value: unknown
): string | null {
  const token = asSettingString(value).trim();
  if (!token) return null;
  const options = strategyRdSettingEnumOptions(setting);
  const match = options.find(
    (row) => row.value === token || row.value === String(Number(token))
  );
  const label = match?.label?.trim();
  return label || null;
}

/** Fractal L=R catalog label — null when value is not a known swing_length option. */
export function strategyRdSwingLengthCatalogLabel(value: unknown): string | null {
  return strategyRdSettingEnumLabel(STRATEGY_RD_SWING_LENGTH_SETTING, value);
}

function polaritySettingRow(
  settings: StrategyRdConceptSetting[] | null | undefined
): StrategyRdConceptSetting | undefined {
  return (settings || []).find((s) => s.key === "polarity" && !s.param);
}

function polaritySettingEnumValues(
  settings: StrategyRdConceptSetting[] | null | undefined
): string[] {
  return strategyRdSettingEnumOptions(polaritySettingRow(settings)).map((row) =>
    row.value.trim().toLowerCase()
  );
}

/** Which top-level `polarity` control the catalog exposes (labels Pattern / Level / Pool). */
export type StrategyRdSetupPolarityKind = "pattern" | "level" | "pool" | "none";

export function strategyRdSetupPolarityKind(
  settings: StrategyRdConceptSetting[] | null | undefined
): StrategyRdSetupPolarityKind {
  const row = polaritySettingRow(settings);
  if (!row) return "none";
  const label = (row.label || "").trim().toLowerCase();
  if (label === "pattern") return "pattern";
  if (label === "level") return "level";
  if (label === "pool") {
    const values = polaritySettingEnumValues(settings);
    // ICT Pool: high/low/both (level-shaped) or buyside/sellside (legacy).
    if (values.some((v) => v === "high" || v === "low")) return "level";
    return "pool";
  }
  const values = polaritySettingEnumValues(settings);
  if (values.some((v) => v === "buyside" || v === "sellside")) return "pool";
  if (values.some((v) => v === "high" || v === "low")) return "level";
  if (values.some((v) => v === "bull" || v === "bear" || v === "bullish" || v === "bearish")) {
    return "pattern";
  }
  return "none";
}

/**
 * Setup Trade side (recipe book) — when Pattern / Level / Pool is on the row,
 * or the catalog ships a Trade side setting (e.g. SMA/EMA cross).
 * Neutrals (doji, inside bar, coil, …) hide it and keep `side: both`.
 */
export function strategyRdSetupShowsTradeSide(
  settings: StrategyRdConceptSetting[] | null | undefined
): boolean {
  if (strategyRdSetupPolarityKind(settings) !== "none") return true;
  return (settings || []).some((s) => (s.key || "").trim() === "side" && !s.param);
}

/** Entry row always uses Trade side (Setup Trade side is hidden on neutrals). */
export function strategyRdEntrySideFieldLabel(_showsSetupTradeSide?: boolean): string {
  return "Trade side";
}

/** Entry side_policy is editable when Setup does not already force Long/Short. */
export function strategyRdEntrySidePolicyEditable(
  setupSide: string | null | undefined,
  showsSetupTradeSide: boolean
): boolean {
  if (!showsSetupTradeSide) return true;
  const side = normalizeStrategyRdSlotSide(setupSide);
  // Invert remaps polarity branches — Entry can stay Both.
  return !side || side === "both" || side === "invert";
}

/** Signal / next-open / limit — Entry Trade side carries direction on neutrals. */
export function strategyRdIsSignalishEntryMode(mode: string | null | undefined): boolean {
  const m = (mode || "").trim().toLowerCase();
  return m === "signal_close" || m === "next_open" || m === "limit_at_level";
}

/** @deprecated Prefer strategyRdSetupPolarityKind(settings) === "pattern". */
export function strategyRdSettingsHasPatternPolarity(
  settings: StrategyRdConceptSetting[] | null | undefined
): boolean {
  return strategyRdSetupPolarityKind(settings) === "pattern";
}

/** @deprecated Prefer strategyRdSetupPolarityKind(settings) === "level". */
export function strategyRdSettingsHasLevelPolarity(
  settings: StrategyRdConceptSetting[] | null | undefined
): boolean {
  return strategyRdSetupPolarityKind(settings) === "level";
}

export function strategyRdSettingsHasPoolPolarity(
  settings: StrategyRdConceptSetting[] | null | undefined
): boolean {
  return strategyRdSetupPolarityKind(settings) === "pool";
}

const POOL_SETUP_IDS = STRATEGY_RD_POOL_SETUP_IDS;

/** Resolve polarity fork kind when compacting a slot without catalog settings in hand. */
export function strategyRdSetupPolarityKindForSetupId(
  setupId: string | null | undefined,
  settings?: StrategyRdConceptSetting[] | null
): StrategyRdSetupPolarityKind {
  const fromSettings = strategyRdSetupPolarityKind(settings);
  if (fromSettings !== "none") return fromSettings;
  const id = (setupId || "").trim();
  if (LEVEL_EXTREME_SETUP_IDS.has(id)) return "level";
  // ICT liq_sweep / liq_run: Pool uses high/low/both (level field shape).
  if (POOL_SETUP_IDS.has(id)) return "level";
  return "none";
}

/** Side options from the catalog `side` setting when present; else Long/Short/Both. */
export function strategyRdSideOptionsFromSettings(
  settings: StrategyRdConceptSetting[] | null | undefined
): StrategyRdSlotSide[] {
  const sideSetting = (settings || []).find((s) => s.key === "side" && !s.param);
  const fromEnum = strategyRdSettingEnumOptions(sideSetting)
    .map((row) => normalizeStrategyRdSlotSide(row.value))
    .filter((v): v is StrategyRdSlotSide => v != null);
  if (fromEnum.length > 0) return fromEnum;
  return ["long", "short", "both"];
}

export function strategyRdDefaultSideFromSettings(
  settings: StrategyRdConceptSetting[] | null | undefined
): StrategyRdSlotSide {
  const sideSetting = (settings || []).find((s) => s.key === "side" && !s.param);
  const fromDefault = normalizeStrategyRdSlotSide(asSettingString(sideSetting?.default));
  if (fromDefault) return fromDefault;
  const options = strategyRdSideOptionsFromSettings(settings);
  return options.includes("both") ? "both" : options[0] || "both";
}
